import { Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SubjectLabel } from '@/components/humint/SubjectLabel'
import { VoicePairEvidence } from '@/components/humint/voice/match/VoicePairEvidence'
import { VoicePairListening } from '@/components/humint/voice/match/VoicePairListening'
import {
  listeningOwed,
  type VoicePairTransport,
} from '@/components/humint/voice/match/voice-pair-model'
import { PAIR_CARD } from '@/components/humint/voice/match/voice-pair-recipes'
import { ShortVoiceEvidence } from '@/components/humint/voice/ShortVoiceEvidence'
import { Button } from '@/components/ui/button'
import { toSubjectSummary } from '@/features/humint/mappers/subject-page'
import type { RecordingVoiceMatch } from '@/services/api/recording-voice-matches'
import { recordingMatchClips } from './recording-match-model'

const COPY = 'identity.detail.audio.match'

export interface RecordingMatchActions {
  busy: boolean
  problem?: string | null
  onDecide: (decision: 'same' | 'different') => void
}

function DecisionButtons({
  actions,
  owed,
}: {
  actions: RecordingMatchActions
  owed: ReturnType<typeof listeningOwed>
}) {
  const { t } = useTranslation()
  return (
    <>
      {owed && (
        <p className="text-sm text-muted-foreground">
          {t(`humint.voice.match.listened.${owed === 'both' ? 'first' : 'firstPlayable'}`)}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {(['same', 'different'] as const).map(decision => {
          const Icon = decision === 'same' ? Check : X
          return (
            <Button
              key={decision}
              variant={decision === 'same' ? 'solid' : 'outline'}
              disabled={Boolean(owed) || actions.busy}
              onClick={() => actions.onDecide(decision)}
            >
              <Icon className="size-4" />
              {t(`humint.voice.match.decision.${decision}`)}
            </Button>
          )
        })}
      </div>
    </>
  )
}

function MatchSource({
  match,
  kind,
}: {
  match: RecordingVoiceMatch
  kind: 'recording' | 'reference'
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2 text-sm">
      <p className="font-semibold">{t(`${COPY}.${kind}`)}</p>
      {kind === 'recording' ? (
        <>
          <p className="break-words">{match.recording.title}</p>
          <p className="text-muted-foreground">
            {t(`${COPY}.voice`, { voice: match.recording.voice })}
          </p>
        </>
      ) : (
        <SubjectLabel subject={toSubjectSummary(match.subject)} order="name-first" />
      )}
      <p className="text-muted-foreground">{t(`${COPY}.source.${kind}`)}</p>
      {kind === 'recording' && match.short_evidence && <ShortVoiceEvidence detail />}
    </div>
  )
}

export function RecordingMatchDecision({
  match,
  transport,
  actions,
}: {
  match: RecordingVoiceMatch
  transport: VoicePairTransport
  actions?: RecordingMatchActions
}) {
  const { t } = useTranslation()
  const owed = listeningOwed(transport.sides)
  const editable = match.member_role !== 'viewer' && match.status === 'suggested' && actions
  return (
    <section className={`${PAIR_CARD} space-y-3 p-4`}>
      <p className="font-medium">{t(`${COPY}.${match.status}`)}</p>
      {match.member_role === 'viewer' && (
        <p className="text-sm text-muted-foreground">
          {t('humint.tattooMatch.decision.readOnly.role')}
        </p>
      )}
      {editable && <DecisionButtons actions={actions} owed={owed} />}
      {actions?.problem && (
        <p role="alert" className="text-sm text-destructive">
          {t(actions.problem)}
        </p>
      )}
    </section>
  )
}

export function RecordingMatchView({
  match,
  transport,
  actions,
}: {
  match: RecordingVoiceMatch
  transport: VoicePairTransport
  actions?: RecordingMatchActions
}) {
  const { t } = useTranslation()
  const clips = recordingMatchClips(match)
  return (
    <div className="@container/voicepair space-y-4">
      <p className="text-sm text-muted-foreground">{t(`${COPY}.hint`)}</p>
      <RecordingMatchDecision match={match} transport={transport} actions={actions} />
      <VoicePairListening
        sides={[
          { ...clips.sides[0], source: <MatchSource match={match} kind="recording" /> },
          { ...clips.sides[1], source: <MatchSource match={match} kind="reference" /> },
        ]}
        transport={transport}
      />
      <VoicePairEvidence pair={{ ...match, seenTogether: false }} />
    </div>
  )
}
